<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import {
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';
import { useCampaignWizard } from '../composables/useCampaignWizard';

const { t } = useI18n();
const { wizardState, availableFields, firstContact } = useCampaignWizard();

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
// GK-HUB: current account to read PIX defaults (merchant_name, pix_key, key_type)
const currentAccount = useMapGetter('getCurrentAccount');

// GK-HUB: PIX field keys for templates with ORDER_DETAILS buttons
const PIX_MAPPING_KEYS = [
  'order_details_pix_code',
  'order_details_amount',
  'order_details_reference_id',
  'order_details_item_name',
  'order_details_body_text',
];

const templateParserRef = ref(null);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() => mapToOptions(inboxes.value, 'id', 'name'));

// GK-HUB: Hide CATALOG/FLOW templates (still unsupported). ORDER_DETAILS templates are supported via PIX sub-form below.
const templateOptions = computed(() => {
  if (!wizardState.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates
    .value(wizardState.inboxId)
    .filter(tmpl => {
      const buttonsComp = tmpl.components?.find(c => c.type === 'BUTTONS');
      if (!buttonsComp) return true;
      return !buttonsComp.buttons?.some(b =>
        ['CATALOG', 'FLOW'].includes(b.type)
      );
    });
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});
// GK-HUB: end template filtering

const selectedTemplate = computed(() => {
  if (!wizardState.templateId) return null;
  const opt = templateOptions.value.find(
    o => o.value === wizardState.templateId
  );
  return opt?.template || null;
});

// GK-HUB: detect templates with ORDER_DETAILS button — triggers PIX sub-form
const hasOrderDetailsButton = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttons = selectedTemplate.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  return !!buttons?.buttons?.some(b => b.type === 'ORDER_DETAILS');
});

const accountPixInfo = computed(() => {
  const attrs = currentAccount.value?.custom_attributes || {};
  return {
    merchant: attrs.pix_merchant_name || '',
    key: attrs.pix_key || '',
    keyType: attrs.pix_key_type || '',
    configured: !!(
      attrs.pix_merchant_name &&
      attrs.pix_key &&
      attrs.pix_key_type
    ),
  };
});
// GK-HUB: end order_details detection

// Sync selectedTemplate to wizardState (separate watcher to avoid side-effect in computed)
watch(selectedTemplate, tmpl => {
  wizardState.selectedTemplate = tmpl;
});

// GK-HUB: Auto-select template when duplicating a campaign
watch(
  templateOptions,
  options => {
    if (
      // eslint-disable-next-line no-underscore-dangle
      !wizardState._prefillTemplateName ||
      wizardState.templateId ||
      options.length === 0
    )
      return;

    const match = options.find(o => {
      const tmpl = o.template;
      return (
        // eslint-disable-next-line no-underscore-dangle
        tmpl.name === wizardState._prefillTemplateName &&
        // eslint-disable-next-line no-underscore-dangle
        (!wizardState._prefillTemplateLanguage ||
          // eslint-disable-next-line no-underscore-dangle
          tmpl.language === wizardState._prefillTemplateLanguage)
      );
    });

    if (match) {
      wizardState.templateId = match.value;
    }

    // eslint-disable-next-line no-underscore-dangle
    delete wizardState._prefillTemplateName;
    // eslint-disable-next-line no-underscore-dangle
    delete wizardState._prefillTemplateLanguage;
  },
  { immediate: true }
);
// GK-HUB: end auto-select template

// Extract body variables from template
const bodyVariables = computed(() => {
  if (!selectedTemplate.value) return [];
  const body = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BODY
  );
  if (!body?.text) return [];
  const matches = body.text.match(/\{\{([^}]+)\}\}/g);
  if (!matches) return [];
  return matches.map(m => m.replace(/\{\{|\}\}/g, ''));
});

const fieldOptions = computed(() =>
  availableFields.value.map(f => ({ value: f, label: f }))
);

// Initialize variable mapping when template changes
watch(
  [bodyVariables, hasOrderDetailsButton],
  ([vars, hasPix]) => {
    const newMapping = {};
    const current = wizardState.variableMapping || {};

    vars.forEach(varKey => {
      newMapping[varKey] = current[varKey] || {
        type: 'fixed',
        value: '',
        field: '',
      };
    });

    // GK-HUB: preserve PIX field mappings when template has ORDER_DETAILS button
    if (hasPix) {
      PIX_MAPPING_KEYS.forEach(pixKey => {
        const defaultType =
          pixKey === 'order_details_amount' ? 'field' : 'fixed';
        newMapping[pixKey] = current[pixKey] || {
          type: defaultType,
          value: '',
          field: '',
        };
      });
    }
    // GK-HUB: end PIX mapping preservation

    wizardState.variableMapping = newMapping;
  },
  { immediate: true }
);

const setMappingType = (varKey, type) => {
  wizardState.variableMapping[varKey] = {
    ...wizardState.variableMapping[varKey],
    type,
  };
};

const setMappingField = (varKey, field) => {
  wizardState.variableMapping[varKey] = {
    ...wizardState.variableMapping[varKey],
    field,
  };
};

const setMappingValue = (varKey, value) => {
  wizardState.variableMapping[varKey] = {
    ...wizardState.variableMapping[varKey],
    value,
  };
};

const formatVarKey = key => '{{' + key + '}}';

// GK-HUB: PIX field labels for ORDER_DETAILS sub-form
const PIX_LABEL_MAP = {
  order_details_pix_code: 'PIX_CODE_LABEL',
  order_details_amount: 'AMOUNT_LABEL',
  order_details_reference_id: 'REFERENCE_ID_LABEL',
  order_details_item_name: 'ITEM_NAME_LABEL',
  order_details_body_text: 'BODY_TEXT_LABEL',
};
const PIX_HINT_MAP = {
  order_details_pix_code: 'PIX_CODE_HINT',
  order_details_amount: 'AMOUNT_HINT',
  order_details_reference_id: 'REFERENCE_ID_HINT',
  order_details_item_name: 'ITEM_NAME_HINT',
  order_details_body_text: '',
};
const pixFieldLabel = key =>
  t(`CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.${PIX_LABEL_MAP[key]}`);
const pixFieldHint = key => {
  const k = PIX_HINT_MAP[key];
  return k ? t(`CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.${k}`) : '';
};
// GK-HUB: end PIX field labels

// Preview: resolve variables using first contact data
const previewText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BODY
  );
  if (!body?.text) return '';

  let text = body.text;
  const mapping = wizardState.variableMapping;
  const contact = firstContact.value || {};

  Object.entries(mapping).forEach(([varKey, config]) => {
    let resolvedValue = '';
    if (config.type === 'field' && config.field) {
      resolvedValue = contact[config.field] || `[${config.field}]`;
    } else if (config.type === 'fixed' && config.value) {
      resolvedValue = config.value;
    } else {
      resolvedValue = `{{${varKey}}}`;
    }
    text = text.replace(`{{${varKey}}}`, resolvedValue);
  });

  return text;
});

// Reset template when inbox changes
watch(
  () => wizardState.inboxId,
  () => {
    wizardState.templateId = null;
    wizardState.selectedTemplate = null;
    wizardState.variableMapping = {};
  }
);

defineExpose({ templateParserRef });
</script>

<template>
  <div class="flex flex-col gap-6">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.STEP_TEMPLATE') }}
    </h3>
    <!-- Inbox + Template Selection -->
    <div class="flex flex-col gap-4 p-4 rounded-xl bg-n-alpha-black2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.SELECT_INBOX') }}
        </label>
        <ComboBox
          v-model="wizardState.inboxId"
          :options="inboxOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.SELECT_TEMPLATE') }}
        </label>
        <ComboBox
          v-model="wizardState.templateId"
          :options="templateOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>
    </div>

    <!-- Template Parser (for media headers and button params — body variables hidden, we use our own mapping) -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      hide-body-variables
      hide-order-details-form
    />

    <!-- Variable Mapping -->
    <div
      v-if="bodyVariables.length > 0"
      class="flex flex-col gap-4 p-4 rounded-xl bg-n-alpha-black2"
    >
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.VARIABLE_MAPPING') }}
      </h4>

      <div
        v-for="varKey in bodyVariables"
        :key="varKey"
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
      >
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <span class="font-mono text-n-teal-11">{{
            formatVarKey(varKey)
          }}</span>
        </div>

        <!-- Type Toggle -->
        <div class="flex gap-2">
          <button
            type="button"
            class="px-3 py-1.5 text-xs rounded-md transition-all"
            :class="
              wizardState.variableMapping[varKey]?.type === 'field'
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="setMappingType(varKey, 'field')"
          >
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING_FIELD') }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-xs rounded-md transition-all"
            :class="
              wizardState.variableMapping[varKey]?.type === 'fixed'
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="setMappingType(varKey, 'fixed')"
          >
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING_FIXED') }}
          </button>
        </div>

        <!-- Field dropdown -->
        <ComboBox
          v-if="wizardState.variableMapping[varKey]?.type === 'field'"
          :model-value="wizardState.variableMapping[varKey]?.field"
          :options="fieldOptions"
          placeholder="Selecionar campo..."
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="val => setMappingField(varKey, val)"
        />

        <!-- Fixed value input -->
        <Input
          v-if="wizardState.variableMapping[varKey]?.type === 'fixed'"
          :model-value="wizardState.variableMapping[varKey]?.value || ''"
          placeholder="Valor fixo..."
          @update:model-value="val => setMappingValue(varKey, val)"
        />
      </div>
    </div>

    <!-- GK-HUB: PIX sub-form (templates with ORDER_DETAILS button) -->
    <div
      v-if="hasOrderDetailsButton"
      class="flex flex-col gap-4 p-4 rounded-xl bg-n-amber-3/20 border border-n-amber-7"
    >
      <div class="flex flex-col gap-1">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.DETECTED_TITLE')
          }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.DETECTED_DESC')
          }}
        </p>
        <p
          v-if="accountPixInfo.configured"
          class="text-xs text-n-slate-10 mt-2"
        >
          {{
            t(
              'CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.ACCOUNT_PIX_INFO',
              {
                merchant: accountPixInfo.merchant,
                key: accountPixInfo.key,
                keyType: accountPixInfo.keyType,
              }
            )
          }}
        </p>
        <p v-else class="text-xs text-n-ruby-10 mt-2">
          {{
            t(
              'CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.ACCOUNT_PIX_MISSING'
            )
          }}
        </p>
      </div>

      <div
        v-for="pixKey in PIX_MAPPING_KEYS"
        :key="pixKey"
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-black2"
      >
        <label class="text-xs font-medium text-n-slate-12">
          {{ pixFieldLabel(pixKey) }}
        </label>
        <p class="text-xs text-n-slate-10">
          {{ pixFieldHint(pixKey) }}
        </p>
        <div class="flex gap-2">
          <button
            type="button"
            class="px-3 py-1.5 text-xs rounded-md transition-all"
            :class="
              wizardState.variableMapping[pixKey]?.type === 'field'
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="setMappingType(pixKey, 'field')"
          >
            {{
              t(
                'CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.MAPPING_TYPE_FIELD'
              )
            }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-xs rounded-md transition-all"
            :class="
              wizardState.variableMapping[pixKey]?.type === 'fixed'
                ? 'bg-n-teal-9 text-white'
                : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="setMappingType(pixKey, 'fixed')"
          >
            {{
              t(
                'CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE_ORDER_DETAILS.MAPPING_TYPE_FIXED'
              )
            }}
          </button>
        </div>

        <ComboBox
          v-if="wizardState.variableMapping[pixKey]?.type === 'field'"
          :model-value="wizardState.variableMapping[pixKey]?.field"
          :options="fieldOptions"
          placeholder="Selecionar coluna..."
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="val => setMappingField(pixKey, val)"
        />
        <Input
          v-if="wizardState.variableMapping[pixKey]?.type === 'fixed'"
          :model-value="wizardState.variableMapping[pixKey]?.value || ''"
          placeholder="Valor fixo..."
          @update:model-value="val => setMappingValue(pixKey, val)"
        />
      </div>
    </div>
    <!-- GK-HUB: end PIX sub-form -->

    <!-- Live Preview -->
    <div
      v-if="selectedTemplate && previewText"
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-black2"
    >
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW_TITLE') }}
      </h4>
      <div
        class="p-3 rounded-lg bg-n-teal-9/10 border border-n-teal-9/20 text-sm text-n-slate-12 whitespace-pre-wrap"
      >
        {{ previewText }}
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW_SUBTITLE') }}
      </p>
    </div>
  </div>
</template>
